import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from "react-leaflet";
import L from "leaflet";
import { useEffect, useMemo } from "react";
import EmployerLogo from "./EmployerLogo";
import { getEmployerLogo, getEmployerLogoFallback, getEmployerInitials } from "../lib/employerLogo";

// Fix default marker icons (Leaflet looks for assets in webpack root by default).
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const navyIcon = L.divIcon({
  className: "",
  html: `<div style="width:28px;height:28px;background:#0B1B3D;border:2px solid #FFB800;border-radius:50%;display:flex;align-items:center;justify-content:center;color:#FFB800;font-weight:700;font-size:12px;font-family:system-ui;">●</div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 14],
});

/* Build a circular logo marker for a TOC. Chains primary (Apistemic) →
 * Google favicon → coloured initials, all via inline JS so the divIcon
 * stays self-contained inside Leaflet. */
function buildLogoIcon(employerName) {
  const url = getEmployerLogo(employerName);
  const fb = getEmployerLogoFallback(employerName);
  const initials = getEmployerInitials(employerName);
  const safeName = (employerName || "").replace(/"/g, "&quot;");
  const fallbackJs = fb
    ? `if(this.dataset.s!='1'){this.dataset.s='1';this.src='${fb}';}else{this.replaceWith(Object.assign(document.createElement('div'),{innerText:'${initials}',style:'width:34px;height:34px;border-radius:50%;background:#0B1B3D;color:#38BDF8;font:700 13px Inter,system-ui,sans-serif;display:flex;align-items:center;justify-content:center;'}));}`
    : `this.replaceWith(Object.assign(document.createElement('div'),{innerText:'${initials}',style:'width:34px;height:34px;border-radius:50%;background:#0B1B3D;color:#38BDF8;font:700 13px Inter,system-ui,sans-serif;display:flex;align-items:center;justify-content:center;'}));`;
  const inner = url
    ? `<img src="${url}" alt="${safeName}" referrerpolicy="no-referrer" style="width:34px;height:34px;object-fit:contain;border-radius:50%;background:#fff;" onerror="${fallbackJs}"/>`
    : `<div style="width:34px;height:34px;border-radius:50%;background:#0B1B3D;color:#38BDF8;font:700 13px Inter,system-ui,sans-serif;display:flex;align-items:center;justify-content:center;">${initials}</div>`;
  return L.divIcon({
    className: "",
    html: `<div style="width:42px;height:42px;border-radius:50%;background:#fff;border:3px solid #0B1B3D;box-shadow:0 4px 10px rgba(0,0,0,0.25);display:flex;align-items:center;justify-content:center;overflow:hidden;">${inner}</div>`,
    iconSize: [42, 42],
    iconAnchor: [21, 21],
    popupAnchor: [0, -18],
  });
}

const amberIcon = L.divIcon({
  className: "",
  html: `<div style="width:32px;height:32px;background:#FFB800;border:3px solid #0B1B3D;border-radius:50%;display:flex;align-items:center;justify-content:center;color:#0B1B3D;font-weight:800;font-size:14px;font-family:system-ui;">★</div>`,
  iconSize: [32, 32],
  iconAnchor: [16, 16],
});

function FitBounds({ origin, depots }) {
  const map = useMap();
  useEffect(() => {
    if (!origin) return;
    const points = [[origin.lat, origin.lng]];
    depots.forEach((d) => points.push([d.lat, d.lng]));
    if (points.length > 1) {
      map.fitBounds(points, { padding: [40, 40], maxZoom: 10 });
    } else {
      map.setView([origin.lat, origin.lng], 9);
    }
  }, [origin, depots, map]);
  return null;
}

export default function DepotMap({ origin, depots = [], minutes = 45, height = 480, allDepots = [] }) {
  const center = origin ? [origin.lat, origin.lng] : [54.5, -3]; // UK centre
  const list = depots.length ? depots : allDepots;
  const iconCache = useMemo(() => {
    const m = new Map();
    list.forEach((d) => {
      if (!m.has(d.employer_name)) m.set(d.employer_name, buildLogoIcon(d.employer_name));
    });
    return m;
  }, [list]);
  return (
    <div className="rounded-lg overflow-hidden border border-border" style={{ height }}>
      <MapContainer center={center} zoom={origin ? 9 : 6} style={{ height: "100%", width: "100%" }} scrollWheelZoom={false}>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {origin && (
          <>
            <Marker position={[origin.lat, origin.lng]} icon={amberIcon}>
              <Popup>Your postcode</Popup>
            </Marker>
            <Circle
              center={[origin.lat, origin.lng]}
              radius={minutes * 800} /* approx 48 km/h avg */
              pathOptions={{ color: "#FFB800", fillColor: "#FFB800", fillOpacity: 0.06, weight: 1.5, dashArray: "6 6" }}
            />
          </>
        )}
        {list.map((d) => (
          <Marker key={d.id} position={[d.lat, d.lng]} icon={iconCache.get(d.employer_name) || navyIcon}>
            <Popup>
              <div className="text-sm" style={{ minWidth: 180 }}>
                <div className="flex items-start gap-2.5">
                  <EmployerLogo name={d.employer_name} size={32} className="shrink-0" />
                  <div className="min-w-0">
                    <div className="font-semibold text-[#0B1B3D] leading-tight">{d.name}</div>
                    <div className="text-xs text-gray-600 mt-0.5">{d.employer_name}</div>
                  </div>
                </div>
                <div className="text-xs mt-2">{d.address}</div>
                {d.duration_minutes != null && (
                  <div className="text-xs mt-1 font-mono">≈ {d.duration_minutes} min · {d.distance_km} km</div>
                )}
              </div>
            </Popup>
          </Marker>
        ))}
        <FitBounds origin={origin} depots={list} />
      </MapContainer>
    </div>
  );
}
